import { DependencyTracker } from "./observables/observable-state.js";
import { _deepEqual } from "./utils";

const NULL_ACTION = {
    type: 'ZERO_INIT',
    payload: {
        owner: null,
        value: null
    },
    event: null
};

const createMicroStore = (initialState, reducer = state => state) => {
    let state = initialState;
    let stateKeys = Object.keys(initialState);
    let subscribers = new Set();
    let isDispatching = false;
    let actionQueue = [];

    const stateProxy = new Proxy(state, {
        get(target, prop) {
            if (DependencyTracker.current) {
                DependencyTracker.current.addDependency({ onValue: (callback) => {
                    subscribers.add(callback);
                    return {
                        unsubscribe: () => subscribers.delete(callback)
                    };
                }});
            }
            return state[prop];
        },
        ownKeys() {
            return stateKeys;
        },
        getOwnPropertyDescriptor(target, prop) {
            return {
                enumerable: true,
                configurable: true,
                value: state[prop]
            };
        }
    });

    function getState() {
        return stateProxy;
    }

    function dispatch(action = NULL_ACTION) {
        if (typeof action !== 'object') {
            console.warn('Invalid action type dispatched, using NULL_ACTION instead:', action);
            action = NULL_ACTION;
        }
        actionQueue.push(action);
        if (!isDispatching) {
            processActionQueue();
        }
    }

    function processActionQueue() {
        if (actionQueue.length === 0) {
            isDispatching = false;
            return;
        }

        isDispatching = true;
        const action = actionQueue.shift() || NULL_ACTION;

        const oldState = { ...state };
        let newState;

        try {
            newState = reducer(state, action);
        } catch (error) {
            console.error('Error in reducer:', error, 'Action:', action);
            newState = oldState; // Revert to old state if an error occurs
        }

        if (!_deepEqual(oldState, newState)) {
            state = newState;
            stateKeys = Object.keys(state);
            subscribers.forEach(subscriber => {
                try {
                    subscriber();
                } catch (error) {
                    console.error('Error in subscriber:', error);
                }
            });
        }

        // Use setTimeout to allow potential new actions to be queued
        // before processing the next action
        requestAnimationFrame(processActionQueue);
    }

    return { getState, dispatch };
}

export { createMicroStore };
